import { useRef, useState } from "react";
import { Copy, ExternalLink, Loader2, Phone, Plus, Reply, Sparkles, X } from "lucide-react";
import { useCreateWaTemplate, useDealerTemplateOptions, useTenantOrganization } from "../../../lib/api";
import { PrimaryButton, SecondaryButton, Select, cardClass, inputClass, textareaClass } from "../primitives";
import WhatsAppPreview from "./WhatsAppPreview";
import { Chips, Field, ReviewList, StepOptionGroups, StepPills, WizardFoot, focusRing } from "./ui";
import { CardsEditor, CodeOptions, MediaPicker, TypePicker } from "./typeParts";
import {
  BUTTON_KINDS,
  CATEGORIES,
  EMPTY_FORM,
  FILE_TYPES,
  LANGUAGES,
  LIMITS,
  buttonRoom,
  edgeVariable,
  hasGap,
  ideas,
  insertAt,
  missing,
  nameProblem,
  namePrefix,
  newButton,
  nextPlaceholder,
  newCard,
  placeholderNumbers,
  stepProblems,
  toPayload,
  typeHasBody,
  typeHasButtons,
  typeLabel,
} from "./templateFormat";

// Only some kinds of template have buttons of their own, so only they get that step.
const stepsFor = (type) => [
  { key: "basics", label: "Basics" },
  { key: "message", label: "Message" },
  ...(typeHasButtons(type) ? [{ key: "buttons", label: "Buttons" }] : []),
  { key: "review", label: "Review" },
];
const PROBLEM_AT = { basics: 0, message: 1, buttons: 2 };
// What a message step can be given to: the ones whose sends fill a body (and a file).
const STEP_TYPES = ["text", "image", "video", "document"];
const KIND_ICON = { QUICK_REPLY: Reply, URL: ExternalLink, PHONE_NUMBER: Phone, COPY_CODE: Copy };

function ButtonEditor({ index, button, onChange, onRemove }) {
  const Icon = KIND_ICON[button.type];
  const set = (field) => (e) => onChange({ ...button, [field]: e.target.value });
  const id = `tpl-button-${index}`;
  return (
    <div className="rounded-xl bg-[#F9FAFB] p-4 ring-1 ring-[#E5E7EB]">
      <div className="mb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-[#374151]">
          <Icon size={15} aria-hidden="true" /> {BUTTON_KINDS[button.type]} button
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove button ${index + 1}`}
          className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[#6B7280] transition-colors duration-150 hover:bg-white hover:text-[#111827] ${focusRing}`}
        >
          <X size={16} />
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {button.type === "COPY_CODE" ? (
          <Field label="Code to copy" htmlFor={`${id}-code`} count={button.example.length} max={LIMITS.copyCode}>
            <input id={`${id}-code`} value={button.example} onChange={set("example")} maxLength={LIMITS.copyCode} placeholder="SAVE20" className={inputClass} />
          </Field>
        ) : (
          <Field label="Button text" htmlFor={`${id}-text`} count={button.text.length} max={LIMITS.buttonText}>
            <input id={`${id}-text`} value={button.text} onChange={set("text")} maxLength={LIMITS.buttonText} className={inputClass} />
          </Field>
        )}
        {button.type === "URL" && (
          <Field label="Link" htmlFor={`${id}-url`}>
            <input id={`${id}-url`} value={button.url} onChange={set("url")} placeholder="https://example.com/track/{{1}}" className={inputClass} />
          </Field>
        )}
        {button.type === "URL" && button.url.includes("{{") && (
          <Field label="Example link" htmlFor={`${id}-example`}>
            <input id={`${id}-example`} value={button.url_example} onChange={set("url_example")} placeholder="https://example.com/track/123" className={inputClass} />
          </Field>
        )}
        {button.type === "PHONE_NUMBER" && (
          <Field label="Phone number" htmlFor={`${id}-phone`}>
            <input id={`${id}-phone`} value={button.phone_number} onChange={set("phone_number")} placeholder="+919876543210" className={inputClass} />
          </Field>
        )}
      </div>
    </div>
  );
}

export default function TemplateComposer({ tenantId, brandName, brandSlug, initial, initialStep = 0, onCreated }) {
  const create = useCreateWaTemplate(tenantId);
  const { data: org } = useTenantOrganization(tenantId);
  const dealers = org?.dealers || [];
  const { data: stepOptions } = useDealerTemplateOptions(tenantId);
  const brandPrefix = namePrefix(brandSlug);
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [step, setStep] = useState(initialStep);
  const steps = stepsFor(form.template_type);
  const LAST = steps.length - 1;
  const at = Math.min(step, LAST);
  const key = steps[at].key;
  const [error, setError] = useState("");
  const [idea, setIdea] = useState("");
  const bodyRef = useRef(null);

  const prefix = form.use_prefix ? brandPrefix : "";
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const numbers = placeholderNumbers(form.body);
  const problems = stepProblems(form);
  const fieldsChosen = !form.template_key || placeholderNumbers(form.body).every((n) => form.field_map[n]);
  // Fields are part of the template; a step is optional.
  const canStep = STEP_TYPES.includes(form.template_type);
  const canNext = key !== "review" ? problems[PROBLEM_AT[key]].length === 0 : missing(form).length === 0 && fieldsChosen;

  const setExample = (n, value) =>
    setForm((f) => {
      const examples = [...f.examples];
      examples[n - 1] = value;
      return { ...f, examples };
    });
  const setType = (type) => {
    setForm((f) => ({
      ...f,
      template_type: type,
      category: type === "authentication" ? "authentication" : f.category === "authentication" ? "utility" : f.category,
      media_url: FILE_TYPES.includes(type) ? f.media_url : "",
      cards: type === "carousel" ? (f.cards.length ? f.cards : [newCard(), newCard()]) : [],
      header_text: type === "text" ? f.header_text : "",
      footer_text: type === "carousel" || type === "authentication" ? "" : f.footer_text,
      buttons: typeHasButtons(type) ? f.buttons : [],
      template_key: STEP_TYPES.includes(type) ? f.template_key : "",
      field_map: STEP_TYPES.includes(type) ? f.field_map : {},
    }));
    setIdea("");
  };
  const setButton = (i, button) => setForm((f) => ({ ...f, buttons: f.buttons.map((b, j) => (j === i ? button : b)) }));

  const applyIdea = (item) => {
    // An idea fills the wording; the prefix choice and the dealer already picked stay.
    setForm((f) => ({ ...item.form, use_prefix: f.use_prefix, dealer_id: f.dealer_id, template_key: f.template_key, field_map: f.field_map, examples: [...item.form.examples], buttons: item.form.buttons.map((b) => ({ ...b })) }));
    setIdea(item.label);
    setError("");
  };

  const addVariable = () => {
    const el = bodyRef.current;
    const { text, caret } = insertAt(form.body, el?.selectionStart, el?.selectionEnd, `{{${nextPlaceholder(form.body)}}}`);
    set({ body: text });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  };

  const send = async () => {
    if (missing(form).length || create.isPending) return;
    setError("");
    try {
      const row = await create.mutateAsync(toPayload(form));
      onCreated?.(row);
      setForm(EMPTY_FORM);
      setStep(0);
      setIdea("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send the template. Try again in a moment.");
    }
  };

  // Enter inside a field does what the main button does.
  const submit = (e) => {
    e.preventDefault();
    if (!canNext) return;
    if (key !== "review") setStep(at + 1);
    else send();
  };

  const type = form.template_type;
  const buttonLabel = (b) => (b.type === "COPY_CODE" ? `Copy ${b.example}` : b.text);
  const review = [
    ["Name", prefix + form.name],
    ["Type", typeLabel(type)],
    ["Dealer", dealers.find((d) => String(d.id) === String(form.dealer_id))?.name || "All dealers"],
    ["Language", LANGUAGES.find(([v]) => v === form.language)?.[1]],
    ["Category", type === "authentication" ? "Authentication" : CATEGORIES.find(([v]) => v === form.category)?.[1]],
    ...(FILE_TYPES.includes(type) ? [["File", form.media_url]] : []),
    ...(type === "text" ? [["Header", form.header_text || "None"]] : []),
    ...(typeHasBody(type) ? [["Message", form.body]] : []),
    ...(type === "carousel" ? [["Cards", `${form.cards.length} cards`]] : []),
    ...(type === "authentication"
      ? [
          ["Button", form.otp.button_text],
          ["Expires", form.otp.code_expiration_minutes ? `After ${form.otp.code_expiration_minutes} minutes` : "Not set"],
        ]
      : []),
    ...(type !== "carousel" && type !== "authentication" ? [["Footer", form.footer_text || "None"]] : []),
    ...(typeHasButtons(type) ? [["Buttons", form.buttons.length ? form.buttons.map(buttonLabel).join(", ") : "None"]] : []),
    ["Sent from", `${brandName}'s Chat360 number`],
  ];

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <form onSubmit={submit} noValidate className={`${cardClass} flex min-w-0 flex-col gap-6 p-6`}>
        <StepPills steps={steps} current={at} onGo={setStep} />

        <div className="flex min-h-[320px] flex-col gap-6">
          {key === "basics" && (
            <>
              <TypePicker value={type} onChange={setType} />
              {type === "text" && (
              <div>
                <div className="mb-[9px] flex items-center gap-2 text-[13.5px] font-semibold text-[#374151]">
                  <Sparkles size={15} className="text-[#2667B5]" aria-hidden="true" /> Start from an idea
                </div>
                <div className="flex flex-wrap gap-2">
                  {ideas(brandName).map((item) => {
                    const on = idea === item.label;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => applyIdea(item)}
                        aria-pressed={on}
                        className={`h-9 cursor-pointer rounded-full border px-4 text-[13.5px] font-medium transition-colors duration-150 ${focusRing} ${
                          on ? "border-[#2667B5] bg-[#EFF5FC] text-[#1F5596]" : "border-[#D1D5DB] bg-white text-[#374151] hover:border-[#9CA3AF]"
                        }`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              )}
              <Field label="Name" htmlFor="tpl-name" error={nameProblem(form.name)}>
                <div className="flex items-stretch">
                  {prefix && (
                    <span className="flex items-center rounded-l-lg border border-r-0 border-[#D1D5DB] bg-[#F3F4F6] px-3 font-mono text-[14px] text-[#6B7280]">{prefix}</span>
                  )}
                  <input
                    id="tpl-name"
                    value={form.name}
                    onChange={(e) => set({ name: e.target.value })}
                    maxLength={LIMITS.name - prefix.length}
                    placeholder="complaint_received"
                    autoComplete="off"
                    spellCheck={false}
                    className={`${inputClass} !h-12 font-mono !text-[14px] ${prefix ? "!rounded-l-none" : ""}`}
                  />
                </div>
                {brandPrefix && (
                  <label className="mt-3 flex cursor-pointer items-center gap-2 text-[13.5px] text-[#374151]">
                    <input type="checkbox" checked={form.use_prefix} onChange={(e) => set({ use_prefix: e.target.checked })} className="h-4 w-4 cursor-pointer accent-[#2667B5]" />
                    Add the brand prefix <span className="font-mono">{brandPrefix}</span>
                  </label>
                )}
              </Field>
              {dealers.length > 0 && (
                <Field label="Dealer" htmlFor="tpl-dealer" optional>
                  <Select id="tpl-dealer" value={String(form.dealer_id)} onChange={(e) => set({ dealer_id: e.target.value })}>
                    <option value="">All dealers</option>
                    {dealers.map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {d.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <div>
                <div className="mb-[9px] text-[13.5px] font-semibold text-[#374151]">Language</div>
                <Chips label="Language" value={form.language} onChange={(language) => set({ language })} options={LANGUAGES} />
              </div>
              {type !== "authentication" && (
                <div>
                  <div className="mb-[9px] text-[13.5px] font-semibold text-[#374151]">Category</div>
                  <Chips label="Category" value={form.category} onChange={(category) => set({ category })} options={CATEGORIES.map(([v, l]) => [v, l])} />
                </div>
              )}
            </>
          )}

          {key === "message" && type === "authentication" && <CodeOptions otp={form.otp} onChange={(otp) => set({ otp })} />}

          {key === "message" && type !== "authentication" && (
            <>
              {FILE_TYPES.includes(type) && <MediaPicker kind={type} id="tpl-file" value={form.media_url} onChange={(media_url) => set({ media_url })} />}
              {type === "text" && (
                <Field label="Header" htmlFor="tpl-header" optional>
                  <input id="tpl-header" value={form.header_text} onChange={(e) => set({ header_text: e.target.value })} maxLength={LIMITS.header} className={inputClass} />
                </Field>
              )}
              <Field label="Message" htmlFor="tpl-body" count={form.body.length} max={LIMITS.body} error={hasGap(form.body) ? "Number the variables 1, 2, 3 with none missing." : ""}>
                <textarea
                  id="tpl-body"
                  ref={bodyRef}
                  value={form.body}
                  onChange={(e) => set({ body: e.target.value })}
                  rows={6}
                  maxLength={LIMITS.body}
                  placeholder="Hello {{1}}, your complaint {{2}} has been logged."
                  className={textareaClass}
                />
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={addVariable}
                    className={`inline-flex h-9 cursor-pointer items-center gap-[6px] rounded-full border border-[#D1D5DB] bg-white px-4 text-[13.5px] font-medium text-[#374151] transition-colors duration-150 hover:border-[#2667B5] hover:text-[#1F5596] ${focusRing}`}
                  >
                    <Plus size={14} aria-hidden="true" /> Add variable {`{{${nextPlaceholder(form.body)}}}`}
                  </button>
                </div>
                {edgeVariable(form.body) && (
                  <div role="note" className="mt-3 rounded-lg bg-[#FFFBEB] px-3 py-2 text-[13px] text-[#92400E]">
                    A message that starts or ends with a variable can be refused. Put a word on each side.
                  </div>
                )}
              </Field>
              {numbers.length > 0 && (
                <div>
                  <div className="mb-[9px] text-[13.5px] font-semibold text-[#374151]">Example values</div>
                  <ul className="m-0 flex list-none flex-col gap-2 p-0">
                    {numbers.map((n) => (
                      <li key={n} className="flex items-center gap-3">
                        <span className="w-[56px] shrink-0 rounded-lg bg-[#EFF5FC] py-[9px] text-center font-mono text-[12.5px] font-medium text-[#1F5596]">{`{{${n}}}`}</span>
                        <input value={form.examples[n - 1] || ""} onChange={(e) => setExample(n, e.target.value)} aria-label={`Example for {{${n}}}`} placeholder="For example, Ravi" className={inputClass} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {type === "carousel" ? (
                <CardsEditor cards={form.cards} onChange={(cards) => set({ cards })} />
              ) : (
                <Field label="Footer" htmlFor="tpl-footer" optional>
                  <input id="tpl-footer" value={form.footer_text} onChange={(e) => set({ footer_text: e.target.value })} maxLength={LIMITS.footer} className={inputClass} />
                </Field>
              )}
            </>
          )}

          {key === "buttons" && (
            <>
              {form.buttons.map((b, i) => (
                <ButtonEditor
                  key={i}
                  index={i}
                  button={b}
                  onChange={(next) => setButton(i, next)}
                  onRemove={() => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, j) => j !== i) }))}
                />
              ))}
              <div className="flex flex-wrap items-center gap-2">
                {Object.keys(BUTTON_KINDS).map((kind) => {
                  const Icon = KIND_ICON[kind];
                  return (
                    <button
                      key={kind}
                      type="button"
                      disabled={buttonRoom(form.buttons, kind) === 0}
                      onClick={() => set({ buttons: [...form.buttons, newButton(kind)] })}
                      className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border border-dashed border-[#9CA3AF] bg-white px-4 text-[13.5px] font-medium text-[#374151] transition-colors duration-150 hover:border-[#2667B5] hover:text-[#1F5596] disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-[#9CA3AF] disabled:hover:text-[#374151] ${focusRing}`}
                    >
                      <Icon size={14} aria-hidden="true" /> {BUTTON_KINDS[kind]}
                    </button>
                  );
                })}
                <span className="ml-1 text-[12.5px] tabular-nums text-[#9CA3AF]">
                  {form.buttons.length}/{LIMITS.buttons}
                </span>
              </div>
            </>
          )}

          {key === "review" && (
            <>
              <ReviewList rows={review} />
              {canStep && (
              <div className="rounded-xl bg-[#F9FAFB] p-4 ring-1 ring-[#E5E7EB]">
                <Field label="Use in a Service journey step" htmlFor="tpl-step" optional>
                  <Select id="tpl-step" value={form.template_key} onChange={(e) => set({ template_key: e.target.value, field_map: {} })}>
                    <option value="">Not yet</option>
                    <StepOptionGroups steps={stepOptions?.steps} />
                  </Select>
                </Field>
                {numbers.length > 0 &&
                  numbers.map((n) => (
                    <div key={n} className="mt-4">
                      <Field label={`{{${n}}} is filled with`} htmlFor={`tpl-field-${n}`}>
                        <Select id={`tpl-field-${n}`} value={form.field_map[n] || ""} onChange={(e) => set({ field_map: { ...form.field_map, [n]: e.target.value } })}>
                          <option value="">Choose a field</option>
                          {(stepOptions?.fields || []).map((f) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                    </div>
                  ))}
              </div>
              )}
              {error && (
                <div role="alert" className="rounded-xl bg-[#FEF2F2] px-4 py-3 text-[14px] leading-5 text-[#991B1B]">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <WizardFoot>
          {at > 0 ? <SecondaryButton onClick={() => setStep(at - 1)}>Back</SecondaryButton> : <span />}
          {key !== "review" ? (
            <PrimaryButton type="submit" disabled={!canNext}>
              {key === "buttons" && form.buttons.length === 0 ? "Skip" : "Continue"}
            </PrimaryButton>
          ) : (
            <PrimaryButton type="submit" disabled={!canNext || create.isPending}>
              {create.isPending && <Loader2 className="animate-spin" size={14} />}
              Send for approval
            </PrimaryButton>
          )}
        </WizardFoot>
      </form>

      <aside className="order-first lg:sticky lg:top-4 lg:order-none" aria-label="Preview">
        <WhatsAppPreview
          brandName={brandName}
          header={form.header_text}
          body={form.body}
          examples={form.examples}
          footer={form.footer_text}
          buttons={form.buttons}
          type={type}
          mediaUrl={form.media_url}
          cards={form.cards}
          otp={form.otp}
        />
      </aside>
    </div>
  );
}
